// src/SuiviInstallations.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { Search, Plus, Save, ChevronDown, ChevronUp, X } from 'lucide-react';

const STATUTS = [
  { key: 'acompte_recu',          label: 'Acompte reçu',            color: '#6366f1', bg: '#eef2ff' },
  { key: 'commande_passee',       label: 'Commande passée',         color: '#f59e0b', bg: '#fffbeb' },
  { key: 'materiel_arrive',       label: 'Matériel arrivé',         color: '#0ea5e9', bg: '#f0f9ff' },
  { key: 'installation_planifiee',label: 'Installation planifiée',  color: '#8b5cf6', bg: '#f5f3ff' },
  { key: 'installation_en_cours', label: 'Installation en cours',   color: '#f97316', bg: '#fff7ed' },
  { key: 'installation_terminee', label: 'Installation terminée',   color: '#10b981', bg: '#f0fdf4' },
  { key: 'solde_recu',            label: 'Solde reçu',              color: '#059669', bg: '#ecfdf5' },
  { key: 'sav',                   label: 'SAV / Garantie',          color: '#ef4444', bg: '#fef2f2' },
];

const STATUT_MAP = Object.fromEntries(STATUTS.map(s => [s.key, s]));

const fmt = (n) => {
  if (!n) return '';
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + ' FCFA';
};

const Badge = ({ statut }) => {
  const s = STATUT_MAP[statut] || STATUTS[0];
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: '11px', fontWeight: 700,
      padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

const SuiviInstallations = ({ clientId = null, compact = false }) => {
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [newInst, setNewInst] = useState({
    client_nom: '', devis_id: '', montant: '', statut: 'acompte_recu',
    date_installation: '', notes: ''
  });

  const fetchInstallations = useCallback(async () => {
    let query = supabase.from('installations').select('*').order('updated_at', { ascending: false });
    if (clientId) query = query.eq('client_id', clientId);
    const { data } = await query;
    if (data) setInstallations(data);
    setLoading(false);
  }, [clientId]);

  useEffect(() => { fetchInstallations(); }, [fetchInstallations]);

  const filtrees = installations.filter(inst => {
    const matchStatut = filtreStatut === 'tous' || inst.statut === filtreStatut;
    const matchSearch = !search ||
      inst.client_nom?.toLowerCase().includes(search.toLowerCase()) ||
      inst.devis_id?.toLowerCase().includes(search.toLowerCase());
    return matchStatut && matchSearch;
  });

  const startEdit = (inst) => {
    setEditingId(inst.id);
    setEditValues({
      statut: inst.statut,
      date_installation: inst.date_installation || '',
      notes: inst.notes || '',
      montant: inst.montant || '',
    });
  };

  const calculerCommission = (ca, p) => {
    let c = 0;
    const t = (taux) => taux / 100;
    if (ca <= 0) return 0;
    c += Math.min(ca, 500000) * t(p.taux_commission_1 || 1.5);
    if (ca > 500000)   c += Math.min(ca - 500000, 1000000)  * t(p.taux_commission_2 || 2.0);
    if (ca > 1500000)  c += Math.min(ca - 1500000, 1500000) * t(p.taux_commission_3 || 2.5);
    if (ca > 3000000)  c += (ca - 3000000)                  * t(p.taux_commission_4 || 3.0);
    return Math.round(c);
  };
  const saveEdit = async (id) => {
    setSaving(true);
    const inst = installations.find(i => i.id === id);
    const ancienStatut = inst?.statut;
    await supabase.from('installations').update({
      statut: editValues.statut,
      date_installation: editValues.date_installation || null,
      notes: editValues.notes,
      montant: parseInt(editValues.montant) || null,
      updated_at: new Date().toISOString(),
    }).eq('id', id);
    // ── Déclencher commission si passage à solde_recu ─────────────────────
    if (editValues.statut === 'solde_recu' && ancienStatut !== 'solde_recu' && inst?.devis_id) {
      const { data: quote } = await supabase.from('quotes')
        .select('id, user_id, total_with_vat, quote_number')
        .eq('quote_number', inst.devis_id).single();
      if (quote?.user_id) {
        const { data: profil } = await supabase.from('profiles').select('*').eq('id', quote.user_id).single();
        if (profil) {
          const mois = new Date().toISOString().slice(0, 7);
          const ca = quote.total_with_vat || parseInt(editValues.montant) || 0;
          const commission = calculerCommission(ca, profil);
          const { data: existing } = await supabase.from('commissions')
            .select('id').eq('quote_id', quote.id).single();
          if (!existing) {
            await supabase.from('commissions').insert({
              user_id: quote.user_id,
              quote_id: quote.id,
              mois,
              ca_installe_paye: ca,
              commission_calculee: commission,
              statut: 'a_verser',
            });
            // Mettre a jour statut du devis
            await supabase.from('quotes').update({ statut: 'installe_paye' }).eq('id', quote.id);
          }
        }
      }
    }
    setSaving(false);
    setEditingId(null);
    fetchInstallations();
  };

  const addInstallation = async () => {
    if (!newInst.client_nom) return;
    await supabase.from('installations').insert({
      ...newInst,
      client_id: clientId || null,
      montant: parseInt(newInst.montant) || null,
      date_installation: newInst.date_installation || null,
    });
    setNewInst({ client_nom: '', devis_id: '', montant: '', statut: 'acompte_recu', date_installation: '', notes: '' });
    setShowAddForm(false);
    fetchInstallations();
  };

  const deleteInstallation = async (id) => {
    if (!window.confirm('Supprimer ce suivi ?')) return;
    await supabase.from('installations').delete().eq('id', id);
    fetchInstallations();
  };

  // Vue compacte pour CRM client
  if (compact) {
    if (loading) return <div style={{ fontSize: '12px', color: '#9ca3af' }}>Chargement...</div>;
    if (installations.length === 0) return (
      <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Aucun suivi d'installation</div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {installations.map(inst => (
          <div key={inst.id} style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 12px',
            border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '12px', color: '#374151' }}>
                  {inst.devis_id ? 'Devis ' + inst.devis_id : 'Installation'}
                </span>
                {inst.montant && <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>{fmt(inst.montant)}</span>}
              </div>
              <Badge statut={inst.statut} />
            </div>
            {inst.date_installation && (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                Date planifiee : {new Date(inst.date_installation).toLocaleDateString('fr-FR')}
              </div>
            )}
            {inst.notes && (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                {inst.notes}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Vue complète pour console manager
  const countByStatut = Object.fromEntries(STATUTS.map(s => [s.key, installations.filter(i => i.statut === s.key).length]));

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif' }}>

      {/* Pipeline visuel */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {STATUTS.map(s => (
          <div key={s.key}
            onClick={() => setFiltreStatut(filtreStatut === s.key ? 'tous' : s.key)}
            style={{ background: filtreStatut === s.key ? s.color : s.bg,
              color: filtreStatut === s.key ? 'white' : s.color,
              border: '1px solid ' + s.color + '40',
              borderRadius: '10px', padding: '8px 14px', cursor: 'pointer',
              transition: 'all 0.15s', textAlign: 'center', minWidth: '100px' }}>
            <div style={{ fontSize: '20px', fontWeight: 800 }}>{countByStatut[s.key] || 0}</div>
            <div style={{ fontSize: '10px', fontWeight: 600, marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Barre de recherche + bouton ajouter */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%',
            transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher client, numéro de devis..."
            style={{ width: '100%', boxSizing: 'border-box', paddingLeft: '32px', paddingRight: '12px',
              paddingTop: '8px', paddingBottom: '8px', border: '1px solid #e5e7eb',
              borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
        </div>
        <button onClick={() => setShowAddForm(!showAddForm)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            background: '#1e40af', color: 'white', border: 'none', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <Plus size={14} /> Ajouter
          {showAddForm ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Formulaire ajout */}
      {showAddForm && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px',
          padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: '#0369a1', marginBottom: '12px' }}>
            Nouveau suivi installation
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Client *</label>
              <input value={newInst.client_nom} onChange={e => setNewInst({...newInst, client_nom: e.target.value})}
                placeholder="Nom du client"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                  border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>N° Devis</label>
              <input value={newInst.devis_id} onChange={e => setNewInst({...newInst, devis_id: e.target.value})}
                placeholder="ex: P210426617"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                  border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Montant (FCFA)</label>
              <input type="number" value={newInst.montant} onChange={e => setNewInst({...newInst, montant: e.target.value})}
                placeholder="ex: 2500000"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                  border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Statut</label>
              <select value={newInst.statut} onChange={e => setNewInst({...newInst, statut: e.target.value})}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb',
                  borderRadius: '6px', fontSize: '12px' }}>
                {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Date installation</label>
              <input type="date" value={newInst.date_installation}
                onChange={e => setNewInst({...newInst, date_installation: e.target.value})}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                  border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Notes</label>
              <input value={newInst.notes} onChange={e => setNewInst({...newInst, notes: e.target.value})}
                placeholder="Remarques..."
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px',
                  border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={addInstallation} disabled={!newInst.client_nom}
              style={{ padding: '8px 20px', background: '#1e40af', color: 'white', border: 'none',
                borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              Enregistrer
            </button>
            <button onClick={() => setShowAddForm(false)}
              style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none',
                borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Compteur */}
      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>
        {filtrees.length} installation(s)
        {filtreStatut !== 'tous' && <span> — filtre : <strong>{STATUT_MAP[filtreStatut]?.label}</strong></span>}
        {filtreStatut !== 'tous' && (
          <button onClick={() => setFiltreStatut('tous')}
            style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer',
              color: '#6b7280', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <X size={11} /> Effacer filtre
          </button>
        )}
      </div>

      {/* Tableau */}
      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
          Chargement...
        </div>
      ) : filtrees.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', background: '#f9fafb',
          borderRadius: '10px', fontSize: '13px' }}>
          Aucune installation trouvée
        </div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#1e40af', color: 'white' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Client</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>N° Devis</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Montant</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Statut</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Date install.</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Notes</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtrees.map((inst, idx) => {
                const isEditing = editingId === inst.id;
                return (
                  <tr key={inst.id} style={{ background: idx % 2 === 0 ? '#fafafa' : 'white',
                    borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e40af' }}>
                      {inst.client_nom}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>
                      {inst.devis_id || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>
                      {isEditing ? (
                        <input type="number" value={editValues.montant}
                          onChange={e => setEditValues({...editValues, montant: e.target.value})}
                          style={{ width: '110px', padding: '4px 8px', border: '1px solid #bfdbfe',
                            borderRadius: '4px', fontSize: '12px', textAlign: 'right' }} />
                      ) : fmt(inst.montant) || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {isEditing ? (
                        <select value={editValues.statut}
                          onChange={e => setEditValues({...editValues, statut: e.target.value})}
                          style={{ padding: '4px 8px', border: '1px solid #bfdbfe',
                            borderRadius: '4px', fontSize: '11px' }}>
                          {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      ) : <Badge statut={inst.statut} />}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>
                      {isEditing ? (
                        <input type="date" value={editValues.date_installation}
                          onChange={e => setEditValues({...editValues, date_installation: e.target.value})}
                          style={{ padding: '4px 8px', border: '1px solid #bfdbfe',
                            borderRadius: '4px', fontSize: '11px' }} />
                      ) : inst.date_installation
                        ? new Date(inst.date_installation).toLocaleDateString('fr-FR')
                        : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#6b7280', maxWidth: '200px' }}>
                      {isEditing ? (
                        <input value={editValues.notes}
                          onChange={e => setEditValues({...editValues, notes: e.target.value})}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px',
                            border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '11px' }} />
                      ) : (
                        <span style={{ fontSize: '11px', fontStyle: inst.notes ? 'normal' : 'italic' }}>
                          {inst.notes || '—'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        {isEditing ? (
                          <>
                            <button onClick={() => saveEdit(inst.id)} disabled={saving}
                              style={{ display: 'flex', alignItems: 'center', gap: '4px',
                                padding: '4px 10px', background: '#1e40af', color: 'white',
                                border: 'none', borderRadius: '4px', fontSize: '11px',
                                fontWeight: 600, cursor: 'pointer' }}>
                              <Save size={12} /> Sauv.
                            </button>
                            <button onClick={() => setEditingId(null)}
                              style={{ padding: '4px 8px', background: '#f3f4f6', color: '#374151',
                                border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>
                              Annuler
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(inst)}
                              style={{ padding: '4px 10px', background: '#eff6ff', color: '#1e40af',
                                border: '1px solid #bfdbfe', borderRadius: '4px',
                                fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                              Modifier
                            </button>
                            <button onClick={() => deleteInstallation(inst.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer',
                                padding: '2px', color: '#ef4444', fontSize: '11px' }}>
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SuiviInstallations;
